import React, { useCallback } from "react";
import { connect } from "react-redux";

import Tag from "antd/es/tag";
import { useIntl } from "react-intl";

import CurrentPlan from "@mapmycustomers/shared/types/plans/CurrentPlan";

import ButtonLink from "@app/component/ButtonLink";
import { formatDate } from "@app/util/formatters";
import openChat from "@app/util/openChat";

import billingAnalytics from "../../billingAnalytics";
import { setAnnual } from "../../store/actions";
import formatPrice from "../../util/formatPrice";
import { SavingsInformation } from "../../util/useSavingsInformation";

import AdvancedPlanInfoCard from "./AdvancedPlanInfoCard";
import BillingInfo from "./BillingInfo";
import styles from "./BillingInformation.module.scss";

interface Props {
  currentPlan: CurrentPlan;
  onAnnualChange: (annual: boolean) => void;
  savingsInformation: SavingsInformation;
}

const BillingInformation: React.FC<Props> = ({
  currentPlan,
  onAnnualChange,
  savingsInformation,
}) => {
  const intl = useIntl();

  const changeToAnnual = useCallback(() => onAnnualChange(true), [onAnnualChange]);

  const currentPrice = {
    ...currentPlan.tier.price,
    value: currentPlan.tier.price.value * currentPlan.licenses,
  };

  const handleBillingQuestions = useCallback(() => {
    billingAnalytics.clicked(["Billing Questions"]);
    openChat();
  }, []);

  return (
    <section className={styles.container}>
      {!currentPlan.tier.isTrial && (
        <div className={styles.licensesCard}>
          <BillingInfo
            text={currentPlan.licenses}
            title={intl.formatMessage({
              id: "billing.information.licenses",
              defaultMessage: "Your Licenses",
              description: "Your licenses card title on Billing page",
            })}
          />
        </div>
      )}

      {!currentPlan.isOldPlan && (
        <div className={styles.rateCard}>
          <BillingInfo
            text={intl.formatMessage(
              {
                id: "billing.information.rate.description",
                defaultMessage:
                  "{isFreeTrial, select, true {Free Trial{expired, select, true { <tag>Expired</tag>} other {}}} other {{price} {interval, select, annual {(per year)} monthly {(per month)} other {}}}}",
                description: "Current Rate card description on Billing page",
              },
              {
                expired: currentPlan.isExpired,
                interval: currentPlan.tier.interval,
                isFreeTrial: currentPlan.tier.isTrial,
                price: formatPrice(intl, currentPrice),
                tag: (text: string) => (
                  <Tag className={styles.expiredTag} color="error">
                    {text}
                  </Tag>
                ),
              }
            )}
            title={intl.formatMessage({
              id: "billing.information.rate",
              defaultMessage: "Current Rate",
              description: "Current Rate card title on Billing page",
            })}
          >
            {savingsInformation.canSave && (
              <ButtonLink onClick={changeToAnnual}>
                {intl.formatMessage(
                  {
                    id: "billing.information.rate.cta",
                    defaultMessage: "Switch to annual and save {value, number, ::percent}",
                    description: "Current Rate card CTA on Billing page",
                  },
                  {
                    value: savingsInformation.savingInPercents,
                  }
                )}
              </ButtonLink>
            )}
          </BillingInfo>
        </div>
      )}

      {!currentPlan.isOldPlan && (
        <div className={styles.dateCard}>
          <BillingInfo
            text={formatDate(currentPlan.nextPaymentDate, "PPP")}
            title={intl.formatMessage(
              {
                id: "billing.information.date",
                defaultMessage:
                  "{isFreeTrial, select, true {Trial End Date} other {Next Payment Due}}",
                description: "Date card title on Billing page",
              },
              {
                isFreeTrial: currentPlan.tier.isTrial,
              }
            )}
          >
            <ButtonLink onClick={handleBillingQuestions}>
              {intl.formatMessage({
                id: "billing.information.date.billingQuestions",
                defaultMessage: "Billing questions?",
                description: "Billing questions link on Date card on Billing page",
              })}
            </ButtonLink>
          </BillingInfo>
        </div>
      )}

      {currentPlan.isOldPlan && (
        <div className={styles.oldPlanCard}>
          <BillingInfo>
            {intl.formatMessage(
              {
                id: "billing.information.oldPlan",
                defaultMessage:
                  "To add a user to your account please <link>contact customer support</link>.",
                description: "Old plan message on the Billing page",
              },
              {
                link: (text: string) => <ButtonLink onClick={openChat}>{text}</ButtonLink>,
              }
            )}
          </BillingInfo>
        </div>
      )}

      <AdvancedPlanInfoCard
        className={styles.advancedPlanCard}
        compactUi={!currentPlan.tier.isTrial && !currentPlan.isOldPlan}
      />
    </section>
  );
};

const mapDispatchToProps = {
  onAnnualChange: setAnnual,
};

export default connect(null, mapDispatchToProps)(BillingInformation);
